import { useEffect, useState } from "react";
import { FileStack } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { useApplicationMutations, useWypisMergeProposal } from "../../hooks";
import { fmtDateTime } from "../../utils";

/** Parse a "1-3, 5" style spec into sorted, deduplicated 1-based page numbers.
 *  Returns null when any chunk is unreadable (the input shows an error state). */
function parseSpec(spec: string, pageCount: number | null): number[] | null {
  const pages = new Set<number>();
  for (const chunk of spec.replace(/;/g, ",").split(",")) {
    const part = chunk.trim();
    if (!part) continue;
    const range = /^(\d+)\s*-\s*(\d+)$/.exec(part);
    if (range) {
      const [a, b] = [Number(range[1]), Number(range[2])];
      if (a < 1 || b < a) return null;
      for (let n = a; n <= b; n++) pages.add(n);
    } else if (/^\d+$/.test(part)) {
      pages.add(Number(part));
    } else {
      return null;
    }
  }
  const out = [...pages].sort((a, b) => a - b);
  if (!out.length) return null;
  if (pageCount && out[out.length - 1] > pageCount) return null;
  return out;
}

/** Wybór stron z PDF-ów wypisów działek wniosku i sklejenie ich w JEDEN załącznik
 *  „wypis" (bez dubli — strona wspólna dla dwóch działek wchodzi raz). Propozycja
 *  stron przychodzi z mapowania stron wypisów; zakresy można poprawić przed sklejeniem. */
export function MergeWypisModal({
  applicationId,
  onClose,
}: {
  applicationId: string;
  onClose: () => void;
}) {
  const { data: proposal, isLoading } = useWypisMergeProposal(applicationId);
  const m = useApplicationMutations(applicationId);
  const [specs, setSpecs] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState("");

  // Prefill: proposed page unions per wypis; wypisy z jakimikolwiek stronami — włączone.
  useEffect(() => {
    if (!proposal) return;
    setSpecs(Object.fromEntries(proposal.wypisy.map((w) => [w.wypis_id, w.proposed_spec])));
    setEnabled(
      Object.fromEntries(proposal.wypisy.map((w) => [w.wypis_id, w.proposed_pages.length > 0])),
    );
  }, [proposal]);

  const rows = (proposal?.wypisy ?? []).map((w) => {
    const spec = specs[w.wypis_id] ?? "";
    const pages = spec.trim() ? parseSpec(spec, w.page_count) : null;
    return { w, spec, pages, invalid: !!spec.trim() && pages === null };
  });
  const active = rows.filter((r) => enabled[r.w.wypis_id] && r.pages?.length);
  const totalPages = active.reduce((sum, r) => sum + (r.pages?.length ?? 0), 0);
  const anyInvalid = rows.some((r) => enabled[r.w.wypis_id] && r.invalid);

  const submit = () => {
    setErr("");
    m.mergeWypis.mutate(
      {
        id: applicationId,
        selections: active.map((r) => ({ wypis: r.w.wypis_id, pages: r.pages! })),
      },
      {
        onSuccess: onClose,
        onError: (e) => {
          const detail = (e as { response?: { data?: { errors?: { detail?: unknown } } } })
            ?.response?.data?.errors?.detail;
          setErr(typeof detail === "string" ? detail : "Sklejanie nie powiodło się.");
        },
      },
    );
  };

  return (
    <Modal open onClose={onClose} title="Sklej wypisy działek w jeden PDF" size="lg">
      <div className="p-6 space-y-3 text-xs overflow-y-auto">
        {isLoading && (
          <div className="py-8 flex justify-center"><Spinner /></div>
        )}
        {proposal && !proposal.wypisy.length && (
          <div className="text-[var(--color-text-muted)]">
            Działki tego wniosku nie mają podpiętych PDF-ów wypisów z mapowaniem stron —
            wgraj wypisy i uzupełnij strony w zakładce Wypisy.
          </div>
        )}
        {proposal && proposal.missing.length > 0 && (
          <div className="text-amber-700">
            ⚠ Bez pokrycia w wypisach: {proposal.missing.map((p) => p.teryt).join(", ")} —
            te działki nie wejdą do sklejonego PDF-a.
          </div>
        )}
        {rows.map(({ w, spec, invalid }) => (
          <div key={w.wypis_id} className="border border-[var(--color-border)] p-2 space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!enabled[w.wypis_id]}
                onChange={(e) => setEnabled((s) => ({ ...s, [w.wypis_id]: e.target.checked }))}
              />
              <b className="truncate">{w.name}</b>
              <span className="text-[var(--color-text-muted)] whitespace-nowrap">
                {w.page_count ? `${w.page_count} str. · ` : ""}wgrano {fmtDateTime(w.created_at)}
              </span>
            </label>
            <div className="pl-5 flex flex-wrap gap-1">
              {w.parcels.map((p) => (
                <Badge key={p.parcel_id + p.teryt} color={p.error ? "red" : "gray"}>
                  {p.teryt}: {p.pages_spec || "—"}
                </Badge>
              ))}
            </div>
            {enabled[w.wypis_id] && (
              <div className="pl-5 flex items-center gap-2">
                <span className="whitespace-nowrap">Strony do sklejenia:</span>
                <input
                  className={`input-base !py-0.5 !w-48 ${invalid ? "!border-red-500" : ""}`}
                  value={spec}
                  placeholder="np. 1-3, 5"
                  onChange={(e) => setSpecs((s) => ({ ...s, [w.wypis_id]: e.target.value }))}
                />
                {invalid && <span className="text-red-600">nieczytelny zakres</span>}
              </div>
            )}
          </div>
        ))}
        {err && <div className="text-red-600">⚠ {err}</div>}
        {proposal && proposal.wypisy.length > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-[var(--color-border)]">
            <span className="text-[var(--color-text-muted)]">
              Razem {totalPages} str. z {active.length} wypisów — duble stron w obrębie
              wypisu odpadają same.
            </span>
            <Button
              size="sm"
              loading={m.mergeWypis.isPending}
              disabled={!totalPages || anyInvalid}
              onClick={submit}
            >
              <FileStack size={13} className="mr-1" /> Sklej i dołącz PDF
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
